import { debounce } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { type MapRef, Map as ReactMapGL, useMap } from 'react-map-gl/mapbox';
import { Loader, Notifications } from 'sonata-react-components';

import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Modals } from '@/router';
import { INTERACTIVE_LAYER_IDS } from '../../layers.const';
import { useMultiFlightMap } from '../../MultiFlightMap.context';
import { ActionToolsPanel } from '../ActionToolsPanel/ActionToolsPanel';
import { AttributionPOI } from '../AttributionPOI/AttributionPOI';
import { CustomNavigationControl } from '../CustomNavigationControl';
import { FileLoader } from '../FileLoader/FileLoader';
import { Legend } from '../Legend/Legend';
import { CartographyLevelsManager } from '../Locations/Cartography/CartographyLevelsManager';
import { AttributionProximityRadiusLayer } from '../Locations/Circles/AttributionProximityRadiusLayer';
import { CircleDrawControl } from '../Locations/Circles/CircleDrawControl';
import { CircleShapeLayer } from '../Locations/Circles/CirclesShapesLayer';
import { PolygonDrawControl } from '../Locations/Polygons/PolygonDrawControl';
import { PolygonEditControl } from '../Locations/Polygons/PolygonEditControl';
import { PolygonShapeLayer } from '../Locations/Polygons/PolygonShapeLayer';
import { useMapPopover } from '../MapPopover/MapPopover.context';
import { PopOverOptions } from '../PopOverOptions/PopOverOptions';
import ScreensLayer from '../Screens/ScreensLayer';
import { TopBar } from '../TopBar/TopBar';
import VenuesMakers from '../Venues/VenuesMarkers';
import { MapViewStyled } from './MapView.style';
import { getMapStyle } from './map.helpers';

import type { GeoJSONSource } from 'mapbox-gl';
import type { ReactNode } from 'react';
import type { MapStyle } from './map.types';

type Props = {
  id: string;
  maxZoom?: number;
  minZoom?: number;
  navigationPosition?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  style?: MapStyle;
  volumetryAndForecast?: ReactNode;
};

type State = {
  initialViewState: {
    latitude: number;
    longitude: number;
    zoom: number;
  };
};

const TOP_POSITION = 65;
const TOP_SCREENS_POSITION = 45;
const LEFT_POSITION = 20;

function MapView({
  id,
  maxZoom = 20,
  minZoom = 0,
  navigationPosition,
  style = 'default',
  volumetryAndForecast
}: Props) {
  const mapRef = useRef() as React.MutableRefObject<MapRef>;
  const { [id]: map } = useMap();
  const { closeMapPopover, isOpen, openMapPopover } = useMapPopover();
  // Below 1750px the file loader would sit under the wide action tools panel, so it goes on top of it.
  const isFileLoaderAboveTools = useMediaQuery('(max-width: 1749px)');
  const mapWrapperRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const { loading, mode, selectedItemId, setList, setMode, setSelectedItemId, type } = useMultiFlightMap();

  const [{ initialViewState }] = useState<State>({
    initialViewState: {
      latitude: 0,
      longitude: 0,
      zoom: 2
    }
  });
  const [position, setPosition] = useState<{ latitude: number; longitude: number } | null>(null);
  const [temporaryPosition, setTemporaryPosition] = useState<{ latitude: number; longitude: number } | null>(null);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: any) => {
      if (event.key === 'Escape' && mode === 'add-venues') {
        setMode(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mode, setMode]);

  const handleOpenMapPopover = (lng: number, lat: number) => {
    if (!map) {
      return;
    }

    const { x, y } = map.project([lng, lat]);
    const mapContainer = map.getContainer();
    const rect = mapContainer.getBoundingClientRect();
    const adjustedX = x + rect.left;
    const adjustedY = y + rect.top;

    openMapPopover({
      render: () => (
        <PopOverOptions
          onClearPositions={() => {
            setPosition(null);
            setTemporaryPosition(null);
          }}
          optionsType="venues"
          position={{
            latitude: lat,
            longitude: lng
          }}
        />
      ),
      x: adjustedX + LEFT_POSITION,
      y: adjustedY - (type === 'dooh' ? TOP_SCREENS_POSITION : TOP_POSITION)
    });
  };

  const handleMapClick = async (event: any) => {
    const feature = event.features?.[0];

    if (feature?.properties?.cluster) {
      const source = map?.getSource(feature.source) as GeoJSONSource | undefined;

      if (typeof source?.getClusterExpansionZoom === 'function') {
        source.getClusterExpansionZoom(feature.properties.cluster_id, (error, nextZoom) => {
          if (error || typeof nextZoom !== 'number') {
            return;
          }
          const currentZoom = map?.getZoom() ?? 0;
          const targetZoom = Math.min(nextZoom > currentZoom ? nextZoom : currentZoom + 1, maxZoom);
          map?.easeTo({ center: feature.geometry.coordinates, zoom: targetZoom });
        });
      }
      return;
    }

    // Stack of screens at the same coordinates: its marker in ScreensLayer opens the list.
    if (feature?.properties?.kind === 'screen' && feature.properties.count > 1) {
      return;
    }

    if (feature?.properties?.kind === 'screen') {
      setSelectedItemId(feature.properties.id);
      setList('screens');
      setMode(null);
      return;
    }

    if (feature?.properties?.kind === 'venue') {
      setSelectedItemId(feature.properties.id);
      setList('venues');
      setMode(null);
      return;
    }

    if (mode === 'add-venues') {
      setPosition({
        latitude: event.lngLat.lat,
        longitude: event.lngLat.lng
      });
      if (isOpen) {
        setTemporaryPosition(null);
      } else {
        setTemporaryPosition({
          latitude: event.lngLat.lat,
          longitude: event.lngLat.lng
        });
      }
      handleOpenMapPopover(event.lngLat.lng, event.lngLat.lat);
    }
    if (isOpen && mode !== 'draw-polygons') {
      closeMapPopover();
    }
    if (selectedItemId) {
      setSelectedItemId(null);
    }
  };

  const handleMouseEnter = (event: any) => {
    if (event.features?.length) {
      setIsHovering(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const handleMouseMove = (event: any) => {
    if (mode === 'add-venues') {
      setPosition({
        latitude: event.lngLat.lat,
        longitude: event.lngLat.lng
      });
    }
  };

  useEffect(() => {
    const resizer = new ResizeObserver(debounce(() => mapRef.current?.resize(), 100));
    resizer.observe(mapWrapperRef.current);
    return () => {
      resizer.disconnect();
    };
  }, []);

  const handleTemporaryPositionChange = (latitude: number, longitude: number) => {
    setTemporaryPosition({
      latitude,
      longitude
    });
    if (map) {
      map.flyTo({
        center: [longitude, latitude],
        curve: 0.8,
        speed: 3,
        zoom: 16
      });
      map.once('zoomend', () => {
        handleOpenMapPopover(longitude, latitude);
      });
    }
  };

  return (
    <>
      <TopBar onTemporaryPositionChange={handleTemporaryPositionChange} volumetryAndForecast={volumetryAndForecast} />
      <MapViewStyled
        className="multiFlightViewMap"
        hasTemporaryPosition={!!temporaryPosition}
        isAddingVenues={mode === 'add-venues'}
        ref={mapWrapperRef}
      >
        {loading && <Loader showBackground type="landing" variant="default" />}
        <ReactMapGL
          attributionControl={false}
          cursor={isHovering ? 'pointer' : ''}
          doubleClickZoom={!isOpen}
          dragPan={!isOpen}
          dragRotate={!isOpen}
          id={id}
          initialViewState={initialViewState}
          interactiveLayerIds={INTERACTIVE_LAYER_IDS}
          logoPosition="bottom-right"
          mapStyle={getMapStyle(style)}
          mapboxAccessToken="pk.eyJ1IjoidGFwdGFwIiwiYSI6IjRSaHpHbUEifQ.2bAMMlWkzbPiVWA6dCE2Wg"
          maxZoom={maxZoom}
          minZoom={minZoom}
          onClick={handleMapClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseMove={handleMouseMove}
          ref={mapRef}
          scrollZoom={!isOpen}
        >
          {navigationPosition && <CustomNavigationControl mapId={id} position={navigationPosition} />}
          <PolygonDrawControl mapId={id} />
          <PolygonEditControl mapId={id} />
          <PolygonShapeLayer mapId={id} />
          <CartographyLevelsManager mapId={id} />
          <CircleShapeLayer mapId={id} />
          <AttributionProximityRadiusLayer />
          <CircleDrawControl mapId={id} />
          <VenuesMakers mapId={id} position={position} temporaryPosition={temporaryPosition} />
          <ScreensLayer mapId={id} />
        </ReactMapGL>
        <div className="multiFlightViewMap__actionToolsPanelContainer">
          {isFileLoaderAboveTools && (
            <div className="multiFlightViewMap__fileLoaderContainer multiFlightViewMap__fileLoaderContainer--aboveTools">
              <FileLoader />
            </div>
          )}
          <ActionToolsPanel />
        </div>
        <div className="multiFlightViewMap__legendContainer">
          <Legend />
        </div>
        {!isFileLoaderAboveTools && (
          <div className="multiFlightViewMap__fileLoaderContainer">
            <FileLoader />
          </div>
        )}
      </MapViewStyled>
      <AttributionPOI />
      <Modals id="cartography-modal" />
      <Modals id="poi-finder-venues-modal" />
      <Modals id="poi-finder-screens-modal" />
      <Modals id="upload-file-modal" />
      <Notifications className="map" portalId="multiFlightNotification" />
    </>
  );
}

export default MapView;
